/**
 * Platform Preferences admin service
 * Service for fetching platform user preferences from the admin API
 */
import type {
  PreferencesRequest,
  PreferencesResponse,
  PreferencesStats,
} from '../Types/Preferences.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const PREFERENCES_ENDPOINT = '/v1/admin/platform-preferences';

/**
 * Get all platform user preferences with optional filters and cursor-based pagination
 */
export const getPlatformPreferences = async (
  params: PreferencesRequest = {},
): Promise<PreferencesResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.nextPageToken) queryParams.append('nextPageToken', params.nextPageToken);
    if (params.language) queryParams.append('language', params.language);
    if (params.theme) queryParams.append('theme', params.theme);
    if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${PREFERENCES_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Failed to fetch platform preferences',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('You do not have permission to view platform preferences');
      }

      throw new Error(errorData.message || 'Failed to fetch platform preferences');
    }

    const data: PreferencesResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Aggregated distribution of themes and languages.
 *
 * Aggregating on the client would mean walking every cursor page of
 * `GET /v1/admin/platform-preferences`; this does it in one query server-side.
 *
 * @param tenantSlug - Restrict to one tenant; omit for every tenant.
 */
export const getPreferencesStats = async (tenantSlug?: string): Promise<PreferencesStats> => {
  const query = tenantSlug ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : '';
  const url = `${env.bffBaseURL}${PREFERENCES_ENDPOINT}/stats${query}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch preferences stats');
  }

  return (await response.json()) as PreferencesStats;
};
